import { css } from '../lib';

const displayFlex = css`
  display: flex;
`;

/*
 * Utilities for controlling the direction of flex items.
 */
const direction = {
  row: css`
    ${displayFlex}
    flex-direction: row;
  `,

  rowReverse: css`
    ${displayFlex}
    flex-direction: row-reverse;
  `,

  column: css`
    ${displayFlex}
    flex-direction: column;
  `,

  columnReverse: css`
    ${displayFlex}
    flex-direction: column-reverse;
  `,
};

/**
 * Utilities for controlling how flex and grid items are positioned along a container's main axis.
 */
const justifyContent = {
  start: css`
    justify-content: flex-start;
  `,

  end: css`
    justify-content: flex-end;
  `,

  center: css`
    justify-content: center;
  `,

  between: css`
    justify-content: space-between;
  `,

  around: css`
    justify-content: space-around;
  `,

  evenly: css`
    justify-content: space-evenly;
  `,
};

/**
 * Utilities for controlling how grid items are aligned along their inline axis.
 */
const justifyItems = {
  start: css`
    justify-items: start;
  `,

  end: css`
    justify-items: end;
  `,

  center: css`
    justify-items: center;
  `,

  stretch: css`
    justify-items: stretch;
  `,
};

/*
 * Utilities for controlling how flex items wrap.
 */
const wrap = {
  wrap: css`
    flex-wrap: wrap;
  `,

  wrapReverse: css`
    flex-wrap: wrap-reverse;
  `,

  nowrap: css`
    flex-wrap: nowrap;
  `,
};

/*
 * Utilities for controlling how flex items both grow and shrink.
 */
const flex = {
  flex1: css`
    flex: 1 1 0%;
  `,

  auto: css`
    flex-wrap: 1 1 auto;
  `,

  initial: css`
    flex-wrap: 0 1 auto;
  `,

  none: css`
    flex: none;
  `,
};

/*
 * Utilities for controlling how flex items grow.
 */
const grow = {
  grow0: css`
    flex-grow: 0;
  `,

  grow: css`
    flex-grow: 1;
  `,
};

/*
 * Utilities for controlling how flex items shrink.
 */
const shrink = {
  shrink0: css`
    flex-shrink: 0;
  `,

  shrink: css`
    flex-shrink: 1;
  `,
};

function contentItemsDistribution(directionOptions: keyof typeof direction) {
  return {
    startCenter: css`
      ${direction[directionOptions]}
      ${justifyContent.start}
      ${justifyItems.center}
    `,

    startStretch: css`
      ${direction[directionOptions]}
      ${justifyContent.start}
      ${justifyItems.stretch}
    `,

    centerCenter: css`
      ${direction[directionOptions]}
      ${justifyContent.center}
      ${justifyItems.center}
    `,

    centerStretch: css`
      ${direction[directionOptions]}
      ${justifyContent.center}
      ${justifyItems.stretch}
    `,

    endCenter: css`
      ${direction[directionOptions]}
      ${justifyContent.end}
      ${justifyItems.center}
    `,

    endStretch: css`
      ${direction[directionOptions]}
      ${justifyContent.end}
      ${justifyItems.stretch}
    `,

    betweenCenter: css`
      ${direction[directionOptions]}
      ${justifyContent.end}
      ${justifyItems.center}
    `,

    betweenStretch: css`
      ${direction[directionOptions]}
      ${justifyContent.end}
      ${justifyItems.stretch}
    `,
  };
}

export const flexbox = {
  row: contentItemsDistribution('row'),
  column: contentItemsDistribution('column'),
  direction,
  flex,
  grow,
  shrink,
  wrap,
};
